import { Define } from '../../../core/decorators';
import { TAG_INPUT_NUMBER } from '../../../core/utils/constants/tags';
import { HTMLBnumInput } from '../input/bnum-input';
import { ATTRIBUTE_TYPE } from '../input/bnum-input.internal/constants';
import { BnumInputNumericCreateOptions } from '../input/bnum-input.internal/types';
import style from './bnum-input-number.less';

const SHEET = HTMLBnumInput.ConstructCSSStyleSheet(style);
const TYPE = 'number';

/**
 * Input nombre.
 *
 * @category Input
 *
 * @structure Sans rien
 * <bnum-input-number></bnum-input-number>
 *
 * @structure Avec une légende
 * <bnum-input-number>Label du champ</bnum-input-number>
 *
 * @structure Avec une légende et un indice
 * <bnum-input-number>
 * Label du champ
 * <span slot="hint">Indice d'utilisation</span>
 * </bnum-input-number>
 *
 * @structure Avec un bouton
 * <bnum-input-number button="true" button-icon="add">Label du champ
 *   <span slot="button">Ajouter</span>
 * </bnum-input-number>
 *
 * @structure En erreur
 * <bnum-input-number min="200" data-value="5">Label du champ
 * </bnum-input-number>
 *
 * @structure Avec un état de succès
 * <bnum-input-number state="success">Label du champ
 *   <span slot="success">Le champ est valide !</span>
 * </bnum-input-number>
 *
 * @structure Avec une icône
 * <bnum-input-number icon="search">Label du champ</bnum-input-number>
 *
 * @structure Avec un bouton avec icône seulement
 * <bnum-input-number placeholder="LA LA !" button-icon="add">Label du champ
 * </bnum-input-number>
 *
 * @structure Désactivé
 * <bnum-input-number disabled>
 *   Label du champ
 * </bnum-input-number>
 *
 * @structure Complet
 * <bnum-input-number
 *   data-value="5"
 *   placeholder="Texte indicatif"
 *   type="text"
 *   state="error"
 *   icon="search"
 *   button="primary"
 *   button-icon="send"
 *   step="10"
 * >
 *   Label du champ
 *   <span slot="hint">Indice d'utilisation</span>
 *   <span slot="success">Le champ est valide !</span>
 *   <span slot="error">Le champ est invalide !</span>
 *   <span slot="button">Envoyer</span>
 * </bnum-input-number>
 *
 * @attr {string} (optional) (default: 'number') type - Type de l'input (text, password, email, etc.) Ne pas modifier, toujours 'number' pour ce composant.
 *
 */
@Define({ tag: TAG_INPUT_NUMBER })
export class HTMLBnumInputNumber extends HTMLBnumInput {
	constructor() {
		super();
	}

	protected _p_getStylesheets(): CSSStyleSheet[] {
		return [...super._p_getStylesheets(), SHEET];
	}

	protected _p_preload(): void {
		this.setAttribute(ATTRIBUTE_TYPE, TYPE);
	}

	/**
	 *@inheritdoc
	 */
	protected _p_buildDOM(): void {
		super._p_buildDOM();
	}

	/**
	 *@inheritdoc
	 */
	static _p_observedAttributes(): string[] {
		return super._p_observedAttributes().filter(x => x !== ATTRIBUTE_TYPE);
	}

	/**
	 * Crée une instance du composant avec les options fournies.
	 * @param label Texte du label principal.
	 * @param options Options d'initialisation (attributs et slots).
	 * @returns Instance du composant.
	 */
	static Create(
		label: string,
		options: BnumInputNumericCreateOptions = {},
	): HTMLBnumInputNumber {
		const finalOptions = {
			type: TYPE,
			...options,
		};
		return super.Create(label, finalOptions) as HTMLBnumInputNumber;
	}

	static get AdditionnalStylesheet(): CSSStyleSheet {
		return SHEET;
	}
}
